import { docTotalFromLines, parsePurchaseMoney, type PurchaseLine } from "./hub-purchase-lines.js";

export type HubInvoiceForReceipt = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	status: string;
	mark?: string;
	extra?: string;
	note: string;
	lines?: PurchaseLine[];
};

export type HubReceiptRow = {
	id?: number;
	party: string;
	doc_at: string;
	ref_no: string;
	against: string;
	method: string;
	amount: string;
	note: string;
	invoiceId?: number | string;
};

const EPSILON = 0.005;

export function invoiceTaxAmount(subtotal: number, mark?: string): number {
	const taxable = mark !== "proforma";
	return taxable ? Math.round(subtotal * 0.08 * 100) / 100 : 0;
}

export function invoiceIdOf(value: number | string | undefined | null): number | null {
	if (value == null || value === "") return null;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

export function invoiceGrandTotal(invoice: HubInvoiceForReceipt): number {
	const subtotal = docTotalFromLines(invoice.lines);
	return subtotal + invoiceTaxAmount(subtotal, invoice.mark);
}

export function invoiceDeletionBlockedReason(
	invoice: HubInvoiceForReceipt,
	receipts: HubReceiptRow[],
): string | null {
	if (invoice.id == null) return null;
	const linked = receiptsForInvoice(receipts, invoice);
	if (linked.length === 0) return null;
	const count = linked.length;
	const noun = count === 1 ? "receipt" : "receipts";
	const sample = linked
		.map((row) => row.ref_no?.trim())
		.filter(Boolean)
		.slice(0, 2);
	const refHint = sample.length ? ` (${sample.join(", ")}${count > sample.length ? ", …" : ""})` : "";
	return `This invoice has ${count} linked ${noun}${refHint}. Delete those receipts first, then try again.`;
}

export function receiptAmount(row: HubReceiptRow): number {
	return parsePurchaseMoney(row.amount);
}

export function receiptsForInvoice(receipts: HubReceiptRow[], invoice: HubInvoiceForReceipt): HubReceiptRow[] {
	const invoiceId = invoice.id;
	return receipts.filter((row) => {
		const linked = invoiceIdOf(row.invoiceId);
		if (invoiceId != null && linked === invoiceId) return true;
		if (linked != null) return false;
		const against = row.against?.trim();
		const ref = invoice.ref_no?.trim();
		return Boolean(against && ref && against === ref);
	});
}

export function paidTotalForInvoice(receipts: HubReceiptRow[], invoice: HubInvoiceForReceipt, excludeReceiptId?: number): number {
	return receiptsForInvoice(receipts, invoice)
		.filter((row) => excludeReceiptId == null || row.id !== excludeReceiptId)
		.reduce((sum, row) => sum + receiptAmount(row), 0);
}

export function invoiceBalanceDue(receipts: HubReceiptRow[], invoice: HubInvoiceForReceipt, excludeReceiptId?: number): number {
	const grand = invoiceGrandTotal(invoice);
	const paid = paidTotalForInvoice(receipts, invoice, excludeReceiptId);
	return Math.max(0, Math.round((grand - paid) * 100) / 100);
}

export function findInvoiceById(invoices: HubInvoiceForReceipt[], id: number): HubInvoiceForReceipt | undefined {
	return invoices.find((row) => row.id === id);
}

export function findInvoiceForReceipt(receipt: HubReceiptRow, invoices: HubInvoiceForReceipt[]): HubInvoiceForReceipt | undefined {
	const id = invoiceIdOf(receipt.invoiceId);
	if (id != null) return findInvoiceById(invoices, id);
	const against = receipt.against?.trim();
	if (!against) return undefined;
	return invoices.find((row) => row.ref_no?.trim() === against);
}

export function findReceiptByKey(receipts: HubReceiptRow[], docId: string | undefined): HubReceiptRow | undefined {
	if (!docId) return undefined;
	const key = decodeURIComponent(docId);
	return receipts.find((row) => String(row.id) === key || row.ref_no?.trim() === key);
}

export function receiptDetailPath(receipt: HubReceiptRow): string {
	const key = encodeURIComponent(receipt.ref_no?.trim() || String(receipt.id ?? ""));
	return `/hub/receipts/${key}`;
}

export function invoiceDetailPathFromInvoice(invoice: HubInvoiceForReceipt, tab = ""): string {
	const key = encodeURIComponent(invoice.ref_no?.trim() || String(invoice.id ?? ""));
	const query = tab ? `?tab=${encodeURIComponent(tab)}` : "";
	return `/hub/invoices/${key}${query}`;
}

export function suggestReceiptRef(receipts: HubReceiptRow[]): string {
	const prefix = "RC-";
	let max = 0;
	for (const row of receipts) {
		const ref = row.ref_no?.trim() ?? "";
		const match = new RegExp(`^${prefix.replace("-", "\\-")}(\\d+)$`, "i").exec(ref);
		if (match) max = Math.max(max, Number(match[1]));
	}
	return `${prefix}${max + 1}`;
}

export function receiptRefIsDuplicate(receipts: HubReceiptRow[], refNo: string, excludeId?: number): boolean {
	const needle = refNo.trim().toLowerCase();
	if (!needle) return false;
	return receipts.some((row) => row.id !== excludeId && row.ref_no?.trim().toLowerCase() === needle);
}

export function validateReceiptPaymentInput(input: {
	amount: string;
	refNo: string;
	receipts: HubReceiptRow[];
	invoice: HubInvoiceForReceipt;
	excludeReceiptId?: number;
}): string | null {
	if (input.invoice.id == null) return "Save the invoice before recording a receipt.";
	const amount = parsePurchaseMoney(input.amount);
	if (amount <= 0) return "Enter a payment amount greater than zero.";
	const ref = input.refNo.trim();
	if (!ref) return "Enter a receipt number.";
	if (receiptRefIsDuplicate(input.receipts, ref, input.excludeReceiptId)) return "That receipt number is already in use.";
	const outstanding = invoiceBalanceDue(input.receipts, input.invoice, input.excludeReceiptId);
	if (amount > outstanding + EPSILON) {
		return `Amount cannot exceed the outstanding balance (${outstanding.toFixed(2)}).`;
	}
	return null;
}

export function invoiceStatusAfterReceipts(invoice: HubInvoiceForReceipt, receipts: HubReceiptRow[]): string {
	const grand = invoiceGrandTotal(invoice);
	const paid = paidTotalForInvoice(receipts, invoice);
	if (paid >= grand - EPSILON) return "paid";
	if (invoice.status === "paid") return "sent";
	return invoice.status;
}

export function buildReceiptRecordFromInvoice(
	invoice: HubInvoiceForReceipt,
	input: {
		docAt: string;
		refNo: string;
		method: string;
		amount: string;
		note: string;
	},
): Record<string, unknown> {
	if (invoice.id == null) throw new Error("Invoice id required");
	return {
		doc_at: input.docAt,
		party: invoice.party,
		ref_no: input.refNo.trim(),
		against: invoice.ref_no?.trim() ?? "",
		method: input.method || "transfer",
		amount: formatReceiptAmount(input.amount),
		note: input.note,
		invoiceId: invoice.id,
	};
}

export function formatReceiptAmount(value: string): string {
	return parsePurchaseMoney(value).toFixed(2);
}

export function receiptRecordFromExisting(receipt: HubReceiptRow, patch: Partial<HubReceiptRow>): Record<string, unknown> {
	const record: Record<string, unknown> = { ...receipt, ...patch };
	delete record.id;
	return record;
}
